import {
  DeleteOutlined,
  EnterOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormItem, WithId } from '@framework/form';
import { FlexRow, VerticalSpace } from '@framework/ui';
import { Button } from 'antd';
import clx from 'classnames';
import React from 'react';
import styled from 'styled-components';
import { FieldDatatype } from '../../../datatype/Datatype';
import { IReference } from '../../../references/IReference';
import { AssignableExpressionInput } from '../../assignable/input/AssignableExpressionInput';
import { ConditionGroupInput } from '../../condition-group/input/ConditionGroupInput';
import { ISwitchExpressionCase } from '../ISwitchExpression';

const SortableSwitchExpressionItemWrapper = styled.div`
  border: 2px solid ${props => props.theme.colorBorder} !important;
  border-radius: ${props => props.theme.borderRadius}px;
  padding: 12px 4px;
  margin-bottom: 12px;
  background-color: ${props => props.theme.colorBgBase};

  & + & {
    border-top: 1px solid ${props => props.theme.colorBorderSecondary}; // #d9d9d9;
    &:last-child {
      padding-bottom: 12px;
    }
  }

  // fix per rimuovere padding top inserito in form-handler
  &&& {
    .ant-form-item {
      padding-top: 0;
    }
  }
`;

const FlexRowWithBorder = styled(FlexRow)`
  position: relative;
  .bordered-flex-col {
    border-left: 1px solid gainsboro;
    border-right: 1px solid gainsboro;
    padding: 0 12px;
    margin: 0 4px;
  }
`;

const IndexWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 32px;
  text-align: center;
  font-size: 16px;
  color: ${props => props.theme.colorText};
  z-index: 1;
`;

const HolderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 18px;
  width: 32px;
  cursor: grab;
  color: ${props => props.theme.colorBorder};
  &:hover {
    color: ${props => props.theme.colorText};
  }
  &.active {
    cursor: grabbing;
  }
`;
const ArrowWrapper = styled.div`
  margin: 0 12px;
  margin-top: -16px;
  font-size: 32px;
  color: ${props => props.theme.colorBorder};
  .anticon {
    transform: scaleX(-1);
  }
`;

function conditionFieldName(name: string, index: number) {
  return `${name}.cases[${index}].condition`;
}

function valueFieldName(name: string, index: number) {
  return `${name}.cases[${index}].value`;
}

export interface ISortableSwitchExpressionItemProps {
  name: string;
  index: number;
  key: string;
  references: IReference[];
  fieldRef: FieldDatatype;
  onRemove: () => void;
  uid: string;
  active: WithId<ISwitchExpressionCase> | null;
}

export function SortableSwitchExpressionItem(
  props: ISortableSwitchExpressionItemProps,
) {
  const { onRemove, key, name, index, references, fieldRef, uid, active } =
    props;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: uid });

  return (
    <SortableSwitchExpressionItemWrapper
      key={key}
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: active?.__uid === uid ? 100 : 0,
      }}
      {...attributes}
    >
      <FlexRowWithBorder
        flexColClassName="bordered-flex-col"
        left={
          <HolderWrapper
            {...listeners}
            className={clx({ active: active?.__uid === uid })}
          >
            <HolderOutlined />
          </HolderWrapper>
        }
        right={
          <Button
            danger
            type="link"
            icon={<DeleteOutlined aria-hidden />}
            onClick={() => onRemove()}
          />
        }
      >
        <IndexWrapper>{index + 1}</IndexWrapper>
        <VerticalSpace size={4} />
        <FlexRow align="center">
          <FormItem
            name={valueFieldName(name, index)}
            label="Risultato della condizione"
            layout={{ removeMargin: true }}
          >
            <AssignableExpressionInput
              size="large"
              block
              references={references}
              name={valueFieldName(name, index)}
              field={fieldRef}
            />
          </FormItem>
        </FlexRow>
        <VerticalSpace size={12} />
        <FlexRow
          align="flex-start"
          left={
            <ArrowWrapper>
              <EnterOutlined />
            </ArrowWrapper>
          }
        >
          <FormItem
            name={conditionFieldName(name, index)}
            label="..."
            layout={{ removeMargin: true, hideVisualLabel: true }}
          >
            <ConditionGroupInput
              name={conditionFieldName(name, index)}
              references={references}
            />
          </FormItem>
        </FlexRow>
      </FlexRowWithBorder>
    </SortableSwitchExpressionItemWrapper>
  );
}
